// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
import { scaleLinear, scaleThreshold, scaleQuantile } from "d3-scale";
import { extent, ticks } from "d3-array";
import { jenks } from "simple-statistics";

/** @typedef {"blue" | "magenta" | "yellow" | "green" | "red" | "gray" | "diverging" } ColorPalette */
/** @typedef {"linear" | "quantile" | "jenks"} ScaleType */

/**
 * @type {Record<ColorPalette, string[]>}
 */
const linearGradients = {
  blue: [urbanColors.blue_shade_lightest, urbanColors.blue, urbanColors.blue_shade_darkest],
  magenta: [
    urbanColors.magenta_shade_lightest,
    urbanColors.magenta,
    urbanColors.magenta_shade_darkest
  ],
  yellow: [urbanColors.yellow_shade_lightest, urbanColors.yellow, urbanColors.yellow_shade_darkest],
  green: [urbanColors.green_shade_lightest, urbanColors.green, urbanColors.green_shade_darkest],
  red: [urbanColors.red_shade_lightest, urbanColors.red, urbanColors.red_shade_darkest],
  gray: [urbanColors.gray_shade_lightest, urbanColors.gray, urbanColors.gray_shade_darkest],
  diverging: [
    urbanColors.yellow_shade_darker,
    urbanColors.yellow,
    urbanColors.white,
    urbanColors.blue,
    urbanColors.blue_shade_darker
  ]
};

/** @type {Record<ColorPalette, string[]>} */
const discreteRamps = {
  blue: [
    urbanColors.blue_shade_lightest,
    urbanColors.blue_shade_light,
    urbanColors.blue,
    urbanColors.blue_shade_dark,
    urbanColors.blue_shade_darkest
  ],
  magenta: [
    urbanColors.magenta_shade_lighter,
    urbanColors.magenta_shade_medium,
    urbanColors.magenta_shade_dark,
    urbanColors.magenta_shade_darker,
    urbanColors.magenta_shade_darkest
  ],
  yellow: [
    urbanColors.yellow_shade_lighter,
    urbanColors.yellow_shade_medium,
    urbanColors.yellow_shade_dark,
    urbanColors.yellow_shade_darker,
    urbanColors.yellow_shade_darkest
  ],
  green: [
    urbanColors.green_shade_lighter,
    urbanColors.green_shade_medium,
    urbanColors.green_shade_dark,
    urbanColors.green_shade_darker,
    urbanColors.green_shade_darkest
  ],
  red: [
    urbanColors.red_shade_lighter,
    urbanColors.red_shade_medium,
    urbanColors.red_shade_dark,
    urbanColors.red_shade_darker,
    urbanColors.red_shade_darkest
  ],
  gray: [
    urbanColors.gray_shade_lighter,
    urbanColors.gray_shade_medium,
    urbanColors.gray_shade_dark,
    urbanColors.gray_shade_darker,
    urbanColors.gray_shade_darkest
  ],
  diverging: urbanColors.getDivergingColors()
};

/**
 * @param {ColorPalette} colorPalette
 * @param {number[]} values
 * @param {[number, number] | undefined} minMax
 * @returns {import("d3-scale").ScaleLinear<string, number, string>}
 */
function getLinearScale(colorPalette, values, minMax) {
  const colors = linearGradients[colorPalette];
  const ext = minMax ? minMax : extent(values);
  const domain = generateBreaks(ext[0] ?? 0, ext[1] ?? 0, colors.length);
  const result = /** @type {import("d3-scale").ScaleLinear<string, number, string>} */ (
    scaleLinear(domain, colors)
  );
  return result;
}

/**
 *
 * @param {number} min
 * @param {number} max
 * @param {number} n
 * @returns
 */
function generateBreaks(min, max, n) {
  // Handle edge cases
  if (n < 2) return [min];

  const step = (max - min) / (n - 1);
  return Array.from({ length: n }, (_, index) => min + step * index);
}

/**
 * @param {ColorPalette} colorPalette
 * @param {number[]} domain
 * @returns {import("d3-scale").ScaleThreshold<number, string, string>}
 */
function getJenksScale(colorPalette, domain) {
  const colors = discreteRamps[colorPalette];
  domain.sort();
  const breaks = jenks(domain, colors.length);
  // const thresholds = breaks.slice(1, -1);
  const thresholds = breaks.slice(1, -1);
  const result = /** @type {import("d3-scale").ScaleThreshold<number, string, string>} */ (
    scaleThreshold(thresholds, colors)
  );
  return result;
}

/**
 * @param {ColorPalette} colorPalette
 * @param {number[]} domain
 * @returns {import("d3-scale").ScaleQuantile<string, string>}
 */
function getQuantileScale(colorPalette, domain) {
  domain.sort();
  const colors = discreteRamps[colorPalette];
  const result = /** @type {import("d3-scale").ScaleQuantile<string, string>} */ (
    scaleQuantile(domain, colors)
  );
  return result;
}

/**
 * @param {ColorPalette} colorPalette
 * @param {number[]} breaks
 * @returns {import("d3-scale").ScaleThreshold<number, string>}
 */
function getThresholdScale(colorPalette, breaks) {
  const colors = discreteRamps[colorPalette];
  const result = /** @type {import("d3-scale").ScaleThreshold<number, string>} */ (
    scaleThreshold(breaks, colors)
  );
  return result;
}

/**
 * @param {ScaleType} scaleType
 * @param {ColorPalette} colorPalette
 * @param {number[]} values
 * @param {number[] | undefined} [customBreaks]
 * @param {[number, number] | undefined} [minMax]
 * @returns {import("d3-scale").ScaleLinear<string, number, string> | import("d3-scale").ScaleThreshold<number, string, string> | import("d3-scale").ScaleQuantile<string, string>}
 */
export function getScale(scaleType, colorPalette, values, customBreaks, minMax) {
  if (customBreaks) {
    return getThresholdScale(colorPalette, customBreaks);
  }
  if (scaleType === "linear") {
    return getLinearScale(colorPalette, values, minMax);
  } else if (scaleType === "quantile") {
    return getQuantileScale(colorPalette, values);
  } else {
    return getJenksScale(colorPalette, values);
  }
}
